import { create } from 'zustand';
import {
  getLeaderboardData,
  getReportCards,
  getDepartmentPerformance,
  getSkillsAssessment,
  getCompletionTrends,
  getCertificationDistribution,
  generateReport,
  downloadReport,
  getKpiSummary,
  getDepartments,
  getEnrollmentFunnel,
  getTimeDistribution,
  getActivityHeatmap,
  getLevelDistribution,
  getCertificationExpiry,
  getAssessmentScores,
  getCohortRetention,
  getTeamPerformance,
  Employee,
  ReportCard,
  DepartmentPerformance,
  SkillScore,
  CompletionTrendData,
  CertificationDistribution,
  GenerateReportParams,
  GenerateReportResponse,
  KpiSummary,
  EnrollmentFunnel,
  TimeDistribution,
  HeatmapCell,
  LevelDistribution,
  CertificationExpiry,
  AssessmentScores,
  CohortRow,
  TeamPerformance,
} from '@/services/PagesServices/SuperAdminReportService';

interface SuperAdminReportAnalyticsState {
  leaderboardData: Employee[] | null;
  reportCards: ReportCard[] | null;
  departmentPerformance: DepartmentPerformance[] | null;
  skillsAssessment: SkillScore[] | null;
  completionTrends: CompletionTrendData[] | null;
  certificationDistribution: CertificationDistribution[] | null;
  loading: boolean;
  generating: boolean;
  error: string | null;

  // extended analytics
  kpiSummary: KpiSummary | null;
  departments: string[] | null;
  enrollmentFunnel: EnrollmentFunnel | null;
  timeDistribution: TimeDistribution | null;
  activityHeatmap: HeatmapCell[] | null;
  levelDistribution: LevelDistribution | null;
  certificationExpiry: CertificationExpiry | null;
  assessmentScores: AssessmentScores | null;
  cohortRetention: CohortRow[] | null;
  teamPerformance: TeamPerformance[] | null;
  analyticsLoading: boolean;
  fetchExtendedAnalytics: () => Promise<void>;

  fetchLeaderboardData: () => Promise<void>;
  fetchReportCards: () => Promise<void>;
  fetchDepartmentPerformance: () => Promise<void>;
  fetchSkillsAssessment: () => Promise<void>;
  fetchCompletionTrends: () => Promise<void>;
  fetchCertificationDistribution: () => Promise<void>;
  fetchAllData: () => Promise<void>;
  generateAndDownloadReport: (params: GenerateReportParams) => Promise<{ success: boolean; error?: string }>;
}

const useSuperAdminReportStore = create<SuperAdminReportAnalyticsState>((set, get) => ({
  leaderboardData: null,
  reportCards: null,
  departmentPerformance: null,
  skillsAssessment: null,
  completionTrends: null,
  certificationDistribution: null,
  loading: false,
  generating: false,
  error: null,

  // extended analytics
  kpiSummary: null,
  departments: null,
  enrollmentFunnel: null,
  timeDistribution: null,
  activityHeatmap: null,
  levelDistribution: null,
  certificationExpiry: null,
  assessmentScores: null,
  cohortRetention: null,
  teamPerformance: null,
  analyticsLoading: false,

  fetchLeaderboardData: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getLeaderboardData();
      set({ leaderboardData: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchReportCards: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getReportCards();
      set({ reportCards: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchDepartmentPerformance: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getDepartmentPerformance();
      set({ departmentPerformance: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchSkillsAssessment: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getSkillsAssessment();
      set({ skillsAssessment: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCompletionTrends: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCompletionTrends();
      set({ completionTrends: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCertificationDistribution: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCertificationDistribution();
      set({ certificationDistribution: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchAllData: async () => {
    try {
      set({ loading: true, error: null });

      // Fetch all data in parallel
      const [
        leaderboardRes,
        reportCardsRes,
        departmentRes,
        skillsRes,
        trendsRes,
        certRes
      ] = await Promise.all([
        getLeaderboardData(),
        getReportCards(),
        getDepartmentPerformance(),
        getSkillsAssessment(),
        getCompletionTrends(),
        getCertificationDistribution()
      ]);

      set({
        leaderboardData: leaderboardRes.data || [],
        reportCards: reportCardsRes.data || [],
        departmentPerformance: departmentRes.data || [],
        skillsAssessment: skillsRes.data || [],
        completionTrends: trendsRes.data || [],
        certificationDistribution: certRes.data || [],
        loading: false
      });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchExtendedAnalytics: async () => {
    set({ analyticsLoading: true });
    const [
      kpi, departments, funnel, timeDist, heatmap,
      levels, certExpiry, assessment, cohort, teams,
    ] = await Promise.all([
      getKpiSummary(),
      getDepartments(),
      getEnrollmentFunnel(),
      getTimeDistribution(),
      getActivityHeatmap(),
      getLevelDistribution(),
      getCertificationExpiry(),
      getAssessmentScores(),
      getCohortRetention(),
      getTeamPerformance(),
    ]);
    set({
      kpiSummary: kpi,
      departments: departments || [],
      enrollmentFunnel: funnel,
      timeDistribution: timeDist,
      activityHeatmap: heatmap || [],
      levelDistribution: levels,
      certificationExpiry: certExpiry,
      assessmentScores: assessment,
      cohortRetention: cohort || [],
      teamPerformance: teams || [],
      analyticsLoading: false,
    });
  },

  generateAndDownloadReport: async (params: GenerateReportParams) => {
    try {
      set({ generating: true, error: null });

      // Generate the report
      const response = await generateReport(params);

      if (!response.success || !response.data) {
        set({ generating: false });
        return {
          success: false,
          error: response.error?.message || 'Failed to generate report'
        };
      }

      // Download the generated report
      const blob = await downloadReport(response.data.fileName);

      if (!blob) {
        set({ generating: false });
        return {
          success: false,
          error: 'Failed to download report'
        };
      }

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      set({ generating: false });
      return { success: true };
    } catch (error: any) {
      set({ generating: false, error: error.message });
      return {
        success: false,
        error: error.message || 'Failed to generate report'
      };
    }
  }
}));

export default useSuperAdminReportStore;
